import { useEffect, useMemo, useState } from "react";
import { BriefcaseBusiness, Mail, Phone, Search, UserCheck } from "lucide-react";
import AdminNavbar from "../../components/layout/AdminNavbar";
import api from "../../api/axios";

const RelievedEmployees = () => {
    const [records, setRecords] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        document.title = "Relieved Employees | Savrion Technologies";
        let ignore = false;

        const fetchRelievedEmployees = async () => {
            try {
                setError("");
                setLoading(true);
                const res = await api.get("/api/admin/relieved-employees");
                if (!ignore) setRecords(Array.isArray(res.data) ? res.data : []);
            } catch (err) {
                if (!ignore) setError(err.response?.data?.message || "Failed to load relieved employees");
            } finally {
                if (!ignore) setLoading(false);
            }
        };

        fetchRelievedEmployees();
        return () => {
            ignore = true;
        };
    }, []);

    const filteredRecords = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return records;
        return records.filter((employee) => [
            employee.name,
            employee.email,
            employee.contact,
            employee.position,
        ].some((value) => String(value || "").toLowerCase().includes(query)));
    }, [records, search]);

    return (
        <div className="flex min-h-screen flex-col bg-[var(--bg-primary)] text-[var(--text-primary)] xl:flex-row">
            <AdminNavbar />

            <main className="min-w-0 flex-1 px-4 py-5 sm:px-6 lg:px-8">
                <section className="mb-5 rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-5 shadow-[var(--shadow-soft)]">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <div className="mb-2 inline-flex items-center gap-2 text-xs font-black uppercase tracking-wide text-[var(--accent-primary)]">
                                <UserCheck size={15} />
                                Archived employee records
                            </div>
                            <h1 className="text-2xl font-black tracking-tight text-neutral-950 dark:text-neutral-50 sm:text-3xl">
                                Relieved Employees
                            </h1>
                            <p className="mt-1 text-sm font-bold text-[var(--text-secondary)]">
                                {records.length} archived record{records.length === 1 ? "" : "s"}
                            </p>
                        </div>

                        <div className="relative w-full lg:w-96">
                            <Search size={17} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search relieved employees"
                                className="h-11 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] pl-10 pr-4 text-sm font-semibold outline-none transition focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)]"
                            />
                        </div>
                    </div>
                </section>

                {error && (
                    <div className="mb-4 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-500">
                        {error}
                    </div>
                )}

                <section className="grid grid-cols-1 gap-3 md:hidden">
                    {filteredRecords.map((employee) => (
                        <article key={employee._id} className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)]">
                            <div className="flex items-start gap-3">
                                <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[var(--accent-primary)]/10 text-sm font-black text-[var(--accent-primary)]">
                                    {employee.name?.charAt(0)?.toUpperCase() || "R"}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <h2 className="text-sm font-black leading-tight">{employee.name || "-"}</h2>
                                    <p className="mt-1 break-all text-xs font-semibold text-[var(--text-secondary)]">{employee.email || "-"}</p>
                                    <div className="mt-3 grid grid-cols-1 gap-2 text-xs">
                                        <div className="flex items-center gap-2 rounded-xl bg-[var(--bg-primary)] px-3 py-2 font-bold">
                                            <Phone size={13} className="text-[var(--accent-primary)]" />
                                            {employee.contact || "-"}
                                        </div>
                                        <div className="flex items-center gap-2 rounded-xl bg-[var(--bg-primary)] px-3 py-2 font-bold">
                                            <BriefcaseBusiness size={13} className="text-[var(--accent-primary)]" />
                                            {employee.position || "-"}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </article>
                    ))}
                </section>

                {!loading && filteredRecords.length === 0 && (
                    <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] px-4 py-14 text-center text-sm font-semibold text-[var(--text-secondary)] md:hidden">
                        No relieved employees found
                    </div>
                )}

                {loading && (
                    <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] px-4 py-14 text-center text-sm font-semibold text-[var(--text-secondary)] md:hidden">
                        Loading relieved employees...
                    </div>
                )}

                <section className="hidden overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-[var(--shadow-soft)] md:block">
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="border-b border-[var(--border-color)] bg-[var(--bg-primary)]">
                                <tr>
                                    {["Name", "Email", "Contact", "Previous Position"].map((head) => (
                                        <th key={head} className="px-4 py-3 text-left text-xs font-black uppercase tracking-wide text-[var(--text-secondary)]">
                                            {head}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRecords.map((employee) => (
                                    <tr key={employee._id} className="border-b border-[var(--border-color)] last:border-0">
                                        <td className="px-4 py-4 text-sm font-black">{employee.name || "-"}</td>
                                        <td className="px-4 py-4">
                                            <span className="inline-flex items-center gap-2 break-all text-sm font-semibold text-[var(--text-secondary)]">
                                                <Mail size={14} className="text-[var(--accent-primary)]" />
                                                {employee.email || "-"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm font-semibold">{employee.contact || "-"}</td>
                                        <td className="px-4 py-4 text-sm font-semibold">{employee.position || "-"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {!loading && filteredRecords.length === 0 && (
                        <div className="border-t border-[var(--border-color)] px-4 py-14 text-center text-sm font-semibold text-[var(--text-secondary)]">
                            No relieved employees found
                        </div>
                    )}

                    {loading && (
                        <div className="border-t border-[var(--border-color)] px-4 py-14 text-center text-sm font-semibold text-[var(--text-secondary)]">
                            Loading relieved employees...
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

export default RelievedEmployees;


